import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Banknote, BookUser, CreditCard, Smartphone } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { ApiError } from "@/api/client";
import { Button, Dialog, TextField } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";
import { customersRepository, type Customer } from "@/repositories/customersRepository";
import { AccountSaleNeedsSignal } from "@/repositories/localSaleRepository";
import type { PaymentMethod } from "@/repositories/salesRepository";

const METHODS: { value: PaymentMethod; label: string; icon: typeof Banknote; key: string }[] = [
  { value: "CASH", label: "Cash", icon: Banknote, key: "F10" },
  { value: "CARD", label: "Card", icon: CreditCard, key: "F11" },
  { value: "MOBILE", label: "Mobile", icon: Smartphone, key: "F12" },
];

const ON_ACCOUNT = { value: "ACCOUNT" as const, label: "On account", icon: BookUser, key: "F9" };

/** Notes and coins a cashier actually holds, for one-tap tendering. */
const DENOMINATIONS = [10, 20, 50, 100, 200];

/**
 * Taking the money.
 *
 * The cash path is the one that has to be fast and forgiving: a queue is waiting,
 * the cashier is looking at the customer rather than the screen, and the number
 * they need is the change. So the tender buttons are large, the common notes are
 * one tap, and the change is the biggest thing on the panel.
 *
 * Card and mobile take the exact amount — there is no change to give — so they
 * skip the tender step entirely rather than making somebody confirm a number
 * that could not have been anything else.
 */
export function PaymentDialog({
  due,
  method: initialMethod,
  onClose,
  onPay,
  pending,
  error,
  allowAccount = false,
}: {
  due: number;
  method: PaymentMethod;
  onClose: () => void;
  onPay: (method: PaymentMethod, tendered: number, customerId?: string) => void;
  pending: boolean;
  error: unknown;
  /** Whether this cashier may put a sale on a customer's account. */
  allowAccount?: boolean;
}) {
  const [method, setMethod] = useState<PaymentMethod>(initialMethod);
  const [tendered, setTendered] = useState<string>("");
  const [customer, setCustomer] = useState<Customer | null>(null);

  const methods = useMemo(
    () => (allowAccount ? [...METHODS, ON_ACCOUNT] : METHODS),
    [allowAccount],
  );
  const isCash = method === "CASH";
  const onAccount = method === "ACCOUNT";
  const available = customer?.account ? Number(customer.account.available_credit) : 0;
  const overLimit = onAccount && customer != null && due > available + 0.0001;
  const canComplete = !onAccount || (customer != null && !overLimit && customer.account?.is_active);
  const tenderedValue = tendered === "" ? due : Number(tendered);
  const change = Math.max(tenderedValue - due, 0);
  const short = tenderedValue < due - 0.0001;

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const match = methods.find((entry) => entry.key === event.key);
      if (match) {
        event.preventDefault();
        setMethod(match.value);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [methods]);

  return (
    <Dialog
      open
      onClose={onClose}
      title="Take payment"
      description={`${formatMoney(due.toFixed(2))} due`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            disabled={pending || (isCash && short) || !canComplete}
            onClick={() => onPay(method, isCash ? tenderedValue : due, customer?.id)}
          >
            {pending ? "Completing…" : `Complete · ${formatMoney(due.toFixed(2))}`}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className={cn("grid gap-2", allowAccount ? "grid-cols-4" : "grid-cols-3")}>
          {methods.map((entry) => (
            <button
              key={entry.value}
              type="button"
              onClick={() => setMethod(entry.value)}
              aria-pressed={method === entry.value}
              className={cn(
                "flex h-touch-lg flex-col items-center justify-center gap-0.5 rounded-control border text-sm font-medium",
                method === entry.value
                  ? "border-brand-500 bg-brand-50 text-brand-700"
                  : "border-line text-ink-600 hover:text-ink-900",
              )}
            >
              <entry.icon className="size-5" aria-hidden />
              {entry.label}
            </button>
          ))}
        </div>

        {isCash ? (
          <div className="space-y-3">
            <TextField
              label="Cash received"
              hint="Leave blank for the exact amount."
              inputMode="decimal"
              autoFocus
              value={tendered}
              onChange={(event) => setTendered(event.target.value)}
              placeholder={due.toFixed(2)}
            />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setTendered(due.toFixed(2))}
                className="h-touch rounded-control border border-line px-4 text-sm font-medium text-ink-600 hover:bg-canvas"
              >
                Exact
              </button>
              {DENOMINATIONS.filter((note) => note >= due).map((note) => (
                <button
                  key={note}
                  type="button"
                  onClick={() => setTendered(note.toFixed(2))}
                  className="h-touch rounded-control border border-line px-4 text-sm font-medium text-ink-600 hover:bg-canvas"
                >
                  R{note}
                </button>
              ))}
            </div>

            <div
              className={cn(
                "flex items-baseline justify-between rounded-card px-4 py-3",
                short ? "bg-danger-50" : "bg-canvas",
              )}
            >
              <span className="text-sm font-medium text-ink-600">
                {short ? "Still needed" : "Change"}
              </span>
              <span
                className={cn(
                  "text-total tabular-nums",
                  short ? "text-danger-700" : "text-brand-600",
                )}
              >
                {formatMoney((short ? due - tenderedValue : change).toFixed(2))}
              </span>
            </div>
          </div>
        ) : onAccount ? (
          <AccountPicker
            due={due}
            chosen={customer}
            onChoose={setCustomer}
            overLimit={overLimit}
            available={available}
          />
        ) : (
          <p className="rounded-card bg-canvas px-4 py-3 text-sm text-ink-600">
            {formatMoney(due.toFixed(2))} exactly. Take the payment on the machine, then complete
            the sale here.
          </p>
        )}

        {error != null && <PaymentError error={error} />}
      </div>
    </Dialog>
  );
}

/**
 * Who the sale goes on the book for.
 *
 * Only account holders are listed, with what they can still spend, read live —
 * the limit is the server's to enforce, and it checks again when the sale goes
 * through. Needs signal; offline the search fails and the sale cannot complete.
 */
function AccountPicker({
  due,
  chosen,
  onChoose,
  overLimit,
  available,
}: {
  due: number;
  chosen: Customer | null;
  onChoose: (customer: Customer | null) => void;
  overLimit: boolean;
  available: number;
}) {
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term, 250);
  const results = useQuery({
    queryKey: ["customers", "on-account", debounced],
    queryFn: () => customersRepository.list({ q: debounced, onAccount: true }),
    retry: false,
  });

  if (chosen) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3 rounded-card bg-canvas px-4 py-3">
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-ink-900">{chosen.name}</span>
            <span className="block text-xs text-ink-500">
              Can still spend {formatMoney(available.toFixed(2))}
            </span>
          </span>
          <Button variant="ghost" size="sm" onClick={() => onChoose(null)}>
            Change
          </Button>
        </div>
        {overLimit && (
          <p className="rounded-card bg-danger-50 px-4 py-3 text-sm text-danger-700">
            {formatMoney(due.toFixed(2))} is more than {chosen.name} can still spend. Take part of
            it another way, or ask the owner about their limit.
          </p>
        )}
        {chosen.account && !chosen.account.is_active && (
          <p className="rounded-card bg-danger-50 px-4 py-3 text-sm text-danger-700">
            This account is stopped
            {chosen.account.suspended_reason ? `: ${chosen.account.suspended_reason}` : "."}
          </p>
        )}
      </div>
    );
  }

  const people = results.data ?? [];
  return (
    <div className="space-y-2">
      <TextField
        label="Whose account?"
        value={term}
        autoFocus
        onChange={(event) => setTerm(event.target.value)}
        placeholder="Name or phone number"
      />
      {results.isError ? (
        <p className="rounded-card bg-warn-50 px-4 py-3 text-sm text-warn-700">
          Accounts need signal. Take another payment, or try again when there is signal.
        </p>
      ) : people.length === 0 && !results.isLoading ? (
        <p className="text-sm text-ink-500">No account holder by that name.</p>
      ) : (
        <ul className="max-h-48 divide-y divide-line overflow-y-auto rounded-card border border-line">
          {people.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                onClick={() => onChoose(person)}
                className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-canvas"
              >
                <span className="min-w-0 truncate text-sm font-medium text-ink-900">
                  {person.name}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-ink-500">
                  {formatMoney(person.account?.available_credit ?? "0.00")} left
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PaymentError({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === "UNDERPAID") {
    return (
      <p className="flex items-start gap-2 rounded-card border border-danger-500/30 bg-danger-50 px-4 py-3 text-sm text-danger-700">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
        {error.detail}
      </p>
    );
  }

  if (error instanceof AccountSaleNeedsSignal) {
    return (
      <p className="rounded-card border border-warn-500/30 bg-warn-50 px-4 py-3 text-sm text-warn-700">
        {error.message}
      </p>
    );
  }

  return (
    <p className="rounded-card border border-danger-500/30 bg-danger-50 px-4 py-3 text-sm text-danger-700">
      {error instanceof ApiError
        ? error.detail
        : "The sale could not be sent. Check the connection and try again — pressing Complete twice is safe."}
    </p>
  );
}
